import React, { useState } from 'react';
import { MapPin, Edit, Trash2, Check, User, CheckCircle, Phone, Navigation, Home, MoreVertical, Loader2, Shield, AlertCircle, Clock, XCircle, Store } from 'lucide-react';
import type { Address, Store as StoreType } from '../../types/index';
import { DeleteConfirmationModal } from './DeleteAddressConfirmationModal';
import { formatDate } from '../../utils/date';
import { useLocationPermission } from '../../hooks/useLocationPermission';
import LocationWarningModal from '../common/LocationWarningModal';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import ConfirmationModal from '../common/ConfirmationModal';

interface AddressCardProps {
  address: Address;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
  onUpdateCoordinates?: (id: number, lat?: number, lng?: number) => void;
  onVerify?: (id: number) => void;
  mode?: 'customer' | 'vendor';
  storeDetail?: StoreType;
  isDefault?: boolean;
  storeAddressId?: number;
}

export const AddressCard: React.FC<AddressCardProps> = ({
  address,
  onEdit,
  onDelete,
  onSetDefault,
  onUpdateCoordinates,
  onVerify,
  mode = 'customer',
  storeDetail,
  isDefault,
  storeAddressId,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [capturedLocation, setCapturedLocation] = useState<{ lat?: number; lng?: number }>({});
  const [locationError, setLocationError] = useState<string | null>(null);
  const [showHomeLocationWarning, setShowHomeLocationWarning] = useState(false);
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const {
    permission,
    error: permissionError,
    requestPermission,
    openSettingsGuide,
    hasPermission,
    isDenied,
  } = useLocationPermission();
  const isStoreAddress = !!storeDetail;
  // Check verification status
  const isHomeAddress = address.label?.toLowerCase() === 'home';
  // const hasLocation = address.latitude && address.longitude;
  // const isVerifiedHome = isHomeAddress && address.is_verified;
  // const isLocationSubmitted = isHomeAddress && hasLocation && !address.is_home_address_verified;
  const needsLocation = address.address_verification_status === 'needs_location';
  // const approved = address.address_verification_status === 'approved';
  const pending = address.address_verification_status === 'pending';
  const rejected = address.address_verification_status === 'rejected';
  const isDefaultAddress = isDefault;

  const handleCaptureLocation = async () => {
    setLocationError(null);

    // Check/request permission first
    const hasPerm = await requestPermission();
    if (!hasPerm) {
      if (isDenied) {
        setLocationError('Permission denied. Please enable location in browser settings.');
      }
      return;
    }

    if (address.label === 'Home') {
      setShowHomeLocationWarning(true);
    } else {
      setShowLocationWarning(true);
      // captureLocation();
    }
  };

  const captureLocation = () => {
    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          await onUpdateCoordinates?.(storeAddressId ? storeAddressId : address.id, latitude, longitude);
        } catch (error) {
          setLocationError('Failed to save coordinates');
        } finally {
          setIsGettingLocation(false);
        }
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case 1:
            setLocationError('Permission denied. Enable location access.');
            openSettingsGuide();
            break;
          case 2: setLocationError('Location unavailable'); break;
          case 3: setLocationError('Request timed out'); break;
          default: setLocationError('Failed to get location');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
    // setShowHomeLocationWarning(false);
    setShowLocationWarning(false);
  }

  return (
    <div className={`border rounded-lg p-4 transition-colors duration-150 ${isDefaultAddress ? 'border-primary-300 bg-primary-50/20' : 'border-gray-200 bg-white'}`}>
      <div className="flex flex-col md:flex-row">
        {/* Left side - Address details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start">
            <div className={`p-2 rounded ${isDefaultAddress ? 'bg-primary-100' : 'bg-gray-100'} mr-3`}>
              {storeDetail ? (
                <Store className="h-4 w-4 text-gray-600" />
              ) : isHomeAddress ? (
                <Home className="h-4 w-4 text-primary-600" />
              ) : (
                <MapPin className="h-4 w-4 text-gray-600" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              {/* Header with label and badges */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h3 className="font-medium text-gray-900">
                  {address.label}
                </h3>
                {storeDetail?.name && (
                  <span className="text-sm text-gray-500">• {storeDetail.name}</span>
                )}
                <div className="flex flex-wrap items-center gap-1">
                  {isDefaultAddress && !storeDetail?.name && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                      Default
                    </span>
                  )}
                  {isDefaultAddress && storeDetail?.name && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                      Default for {storeDetail.name}
                    </span>
                  )}
                  {/* Verification Badges */}
                  {!isStoreAddress && address.is_verified && isHomeAddress && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified Home
                    </span>
                  )}

                  {/* Verified Address (non-home) */}
                  {!isStoreAddress && address.is_verified && !isHomeAddress && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified
                    </span>
                  )}

                  {!isStoreAddress && pending && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      <Clock className="h-3 w-3 mr-1" />
                      Submitted for Verification
                    </span>
                  )}

                  {/* Rejected Badge */}
                  {rejected && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-red-800">
                          <p className="font-medium mb-1">This address was rejected</p>
                          <p className="mb-2">Please edit the address to fix the issues below, then save again to resubmit for verification.</p>
                          {address.rejection_reason && (
                            <div className="mt-2 p-2 bg-white rounded border border-red-200 text-xs">
                              <span className="font-medium">Reason:</span> {address.rejection_reason}
                            </div>
                          )}
                          <button
                            onClick={() => { onEdit(); }}
                            className="mt-2 inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit Address
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {needsLocation && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Location Required
                    </span>
                  )}

                </div>
              </div>

              {/* Verification Notice - Only show if location required */}
              {needsLocation && address.label === 'Home' && (
                <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                  <p className="text-amber-800">
                    <span className="font-medium">Save location</span> to enable home address verification for payment on delivery.
                  </p>
                </div>
              )}

              {/* Recipient Name */}
              <div className="flex items-center mb-2">
                <User className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
                <span className="text-sm text-gray-900">{address.recipient_name}</span>
              </div>

              {/* Contact Numbers */}
              <div className="space-y-1 mb-3">
                <div className="flex items-center">
                  <Phone className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
                  <span className="text-sm text-gray-700">
                    {formatPhoneNumber(address.contact_number_1)}
                  </span>
                </div>

                {address.contact_number_2 && (
                  <div className="flex items-center">
                    <Phone className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
                    <span className="text-sm text-gray-600">
                      {formatPhoneNumber(address.contact_number_2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Address Details */}
              <div className="mb-3">
                <div className="text-sm text-gray-700 mb-0.5">
                  {address.house_number && (
                    <span className="font-medium">{address.house_number} </span>
                  )}
                  {address.address_line_1},
                </div>

                {address.address_line_2 && (
                  <div className="text-sm text-gray-700 mb-0.5">
                    {address.address_line_2},
                  </div>
                )}

                <div className="text-sm font-medium text-gray-900">
                  {address.city_details?.name || address.city}
                </div>
              </div>

              {/* Delivery Note */}
              {address.delivery_note && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium text-gray-700">Note:</span>{' '}
                    {address.delivery_note}
                  </p>
                </div>
              )}

              {/* Set Default Button - FIXED HEIGHT */}
              <div className="mt-3 h-6">
                {!isDefaultAddress ? (
                  <button
                    onClick={onSetDefault}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Set as Default {storeDetail?.name ? `for ${storeDetail.name}` : 'Address'}
                  </button>
                ) : (
                  <div className="invisible text-xs">Placeholder</div>
                )}
              </div>
            </div>
          </div>

          {/* Coordinates Section */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Navigation className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Location</p>
                  {(address.latitude && address.longitude && address.coordinates_saved_at) ? (
                    <p className="text-xs text-gray-500">
                      Saved {formatDate(address.coordinates_saved_at)}
                      {pending && (
                        <span className="ml-1 text-blue-600">• Submitted for verification</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600">
                      No coordinates saved
                    </p>
                  )}
                </div>
              </div>

              {!address.latitude && onUpdateCoordinates && (
                <div className="flex flex-col items-end">
                  {isDenied ? (
                    <div className="text-right">
                      <button
                        onClick={openSettingsGuide}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                      >
                        Enable Location Access
                      </button>
                      <p className="text-xs text-gray-500 mt-1">Required for accurate delivery</p>
                    </div>
                  ) : (
                    <button
                      onClick={handleCaptureLocation}
                      disabled={isGettingLocation}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isGettingLocation ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Getting Location...
                        </>
                      ) : (
                        'Save Location'
                      )}
                    </button>
                  )}
                  {locationError && (
                    <p className="text-xs text-red-600 mt-1">{locationError}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Action buttons */}
        <div className="relative ml-2">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {/* Dropdown Actions */}
          {showActions && (
            <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border py-1 z-10">
              {rejected || mode === 'vendor' && (
                <>
                  <button
                    onClick={() => { onEdit(); setShowActions(false); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Edit className="h-3.5 w-3.5 mr-2 text-gray-400" />
                    Edit
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                </>
              )}

              {/* About Verification Option */}
              {!isStoreAddress && isHomeAddress && needsLocation && (
                <>
                  <button
                    onClick={() => {
                      const message = needsLocation
                        ? 'Save location data first, then your home address will be submitted for verification. This ensures driver safety for cash-on-delivery.'
                        : 'Your home address with location data will be verified shortly. This process ensures driver safety for cash-on-delivery orders.';
                      alert(message);
                      setShowActions(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center"
                  >
                    <CheckCircle className="h-3.5 w-3.5 mr-2" />
                    About Verification
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                </>
              )}

              {/* Admin Verify Option */}
              {onVerify && !address.is_verified && (
                <>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={() => { onVerify(address.id); setShowActions(false); }}
                    className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center"
                  >
                    <CheckCircle className="h-3.5 w-3.5 mr-2" />
                    Verify
                  </button>
                </>
              )}

              {/* <div className="border-t border-gray-100 my-1"></div> */}

              <button
                onClick={() => { setShowDeleteModal(true); setShowActions(false); }}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={onDelete}
      />
      {/* Home Location Warning modal */}
      {showHomeLocationWarning && (
        <LocationWarningModal
          isOpen={showHomeLocationWarning}
          onClose={() => setShowHomeLocationWarning(false)}
          onConfirm={captureLocation}
        />)}

      {/* General Location Warning modal */}
      {showLocationWarning && (
        <ConfirmationModal
          isOpen={showLocationWarning}
          onClose={() => {
            setShowLocationWarning(false);
          }}
          onConfirm={captureLocation}
          title="Confirm Your Location"
          message={
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                You're about to save your current location for:
              </p>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-800">Address:</p>
                <p className="text-sm text-blue-700">
                  {[
                    address.house_number,
                    address.address_line_1,
                    address.city
                  ].filter(Boolean).join(', ')}
                </p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Navigation className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Are you at this location?
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {mode === 'vendor'
                        ? 'Your GPS coordinates will be saved for pickup verification and driver navigation.'
                        : 'Your GPS coordinates will be saved for delivery verification.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          }
          confirmText="Yes, I'm Here"
          cancelText="Cancel"
          type="info"
          isProcessing={isGettingLocation}
        />
      )}
    </div>
  );
};

export default AddressCard;